import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = '';
const TC = '#1565c0';
const TB = '#e3f2fd';
const TC2 = '#0d47a1';

const SAMPLE_DATA = [
  { label: '매출 데이터', data: '월,매출액,비용,이익\n1월,5200,3100,2100\n2월,4800,2900,1900\n3월,6100,3400,2700\n4월,5700,3200,2500\n5월,7200,3800,3400\n6월,6800,3600,3200\n7월,7500,4000,3500\n8월,8100,4200,3900\n9월,7800,4100,3700\n10월,8500,4300,4200\n11월,9200,4600,4600\n12월,10100,5000,5100' },
  { label: '부서별 인원', data: '부서,인원,평균연봉(만원),만족도\n개발팀,45,5200,4.2\n디자인팀,12,4600,4.5\n마케팅팀,18,4400,3.8\n영업팀,25,4800,3.6\n인사팀,8,4300,4.1\n재무팀,10,4700,3.9' },
  { label: '제품별 판매량', data: '제품,Q1,Q2,Q3,Q4\n제품A,1200,1500,1800,2100\n제품B,800,950,1100,1300\n제품C,2000,1800,2200,2500\n제품D,600,700,650,800\n제품E,1500,1700,1900,2300' },
  { label: '고객 만족도 조사', data: '항목,매우불만,불만,보통,만족,매우만족\n제품품질,2,5,15,45,33\n고객서비스,3,8,20,40,29\n가격경쟁력,5,12,30,35,18\n배송속도,1,4,10,38,47\n사후관리,4,10,25,38,23' },
];

const CHART_COLORS = ['#1565c0','#e53935','#43a047','#fb8c00','#8e24aa','#00acc1','#6d4c41','#546e7a','#d81b60','#00897b','#fdd835','#3949ab'];

/* ─── SVG 차트 컴포넌트들 ─── */

function BarChart({ data, width = 600, height = 350 }) {
  if (!data || !data.labels || !data.datasets) return null;
  const padding = { top: 40, right: 30, bottom: 60, left: 70 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const allValues = data.datasets.flatMap(ds => ds.values);
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(0, ...allValues);
  const range = maxVal - minVal || 1;
  const groupCount = data.labels.length;
  const barCount = data.datasets.length;
  const groupWidth = chartW / groupCount;
  const barWidth = Math.min(groupWidth * 0.7 / barCount, 40);
  const gridLines = 5;

  return (
    <svg width={width} height={height} style={{ display: 'block', margin: '0 auto' }}>
      {[...Array(gridLines + 1)].map((_, i) => {
        const y = padding.top + (chartH * i / gridLines);
        const val = Math.round(maxVal - (range * i / gridLines));
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e0e0e0" strokeDasharray="3,3" />
            <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize={11} fill="#666">{val.toLocaleString()}</text>
          </g>
        );
      })}
      <line x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} stroke="#bbb" />
      <line x1={padding.left} y1={padding.top} x2={padding.left} y2={height - padding.bottom} stroke="#bbb" />
      {data.datasets.map((ds, di) =>
        ds.values.map((val, vi) => {
          const x = padding.left + groupWidth * vi + (groupWidth - barWidth * barCount) / 2 + barWidth * di;
          const barH = ((val - minVal) / range) * chartH;
          const y = padding.top + chartH - barH;
          return (
            <g key={`${di}-${vi}`}>
              <rect x={x} y={y} width={barWidth} height={barH} fill={CHART_COLORS[di % CHART_COLORS.length]} rx={2} opacity={0.85}>
                <title>{ds.name}: {val.toLocaleString()}</title>
              </rect>
              {barCount === 1 && <text x={x + barWidth / 2} y={y - 4} textAnchor="middle" fontSize={10} fill="#333">{val.toLocaleString()}</text>}
            </g>
          );
        })
      )}
      {data.labels.map((label, i) => (
        <text key={i} x={padding.left + groupWidth * i + groupWidth / 2} y={height - padding.bottom + 18} textAnchor="middle" fontSize={11} fill="#555">
          {label.length > 8 ? label.slice(0, 8) + '..' : label}
        </text>
      ))}
      {data.datasets.length > 1 && (
        <g transform={`translate(${padding.left}, ${padding.top - 25})`}>
          {data.datasets.map((ds, i) => (
            <g key={i} transform={`translate(${i * 100}, 0)`}>
              <rect width={12} height={12} fill={CHART_COLORS[i % CHART_COLORS.length]} rx={2} />
              <text x={16} y={10} fontSize={11} fill="#555">{ds.name}</text>
            </g>
          ))}
        </g>
      )}
    </svg>
  );
}

function LineChart({ data, width = 600, height = 350 }) {
  if (!data || !data.labels || !data.datasets) return null;
  const padding = { top: 40, right: 30, bottom: 60, left: 70 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const allValues = data.datasets.flatMap(ds => ds.values);
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(0, ...allValues);
  const range = maxVal - minVal || 1;
  const n = data.labels.length;
  const gridLines = 5;

  const getX = i => padding.left + (chartW * i / (n - 1 || 1));
  const getY = v => padding.top + chartH - ((v - minVal) / range) * chartH;

  return (
    <svg width={width} height={height} style={{ display: 'block', margin: '0 auto' }}>
      {[...Array(gridLines + 1)].map((_, i) => {
        const y = padding.top + (chartH * i / gridLines);
        const val = Math.round(maxVal - (range * i / gridLines));
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e0e0e0" strokeDasharray="3,3" />
            <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize={11} fill="#666">{val.toLocaleString()}</text>
          </g>
        );
      })}
      {data.datasets.map((ds, di) => {
        const points = ds.values.map((v, i) => `${getX(i)},${getY(v)}`).join(' ');
        const color = CHART_COLORS[di % CHART_COLORS.length];
        return (
          <g key={di}>
            <polyline points={points} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" />
            {ds.values.map((v, i) => (
              <g key={i}>
                <circle cx={getX(i)} cy={getY(v)} r={4} fill="white" stroke={color} strokeWidth={2}>
                  <title>{ds.name}: {v.toLocaleString()}</title>
                </circle>
              </g>
            ))}
          </g>
        );
      })}
      {data.labels.map((label, i) => (
        <text key={i} x={getX(i)} y={height - padding.bottom + 18} textAnchor="middle" fontSize={11} fill="#555">
          {label.length > 8 ? label.slice(0, 8) + '..' : label}
        </text>
      ))}
      {data.datasets.length > 1 && (
        <g transform={`translate(${padding.left}, ${padding.top - 25})`}>
          {data.datasets.map((ds, i) => (
            <g key={i} transform={`translate(${i * 100}, 0)`}>
              <rect width={12} height={12} fill={CHART_COLORS[i % CHART_COLORS.length]} rx={2} />
              <text x={16} y={10} fontSize={11} fill="#555">{ds.name}</text>
            </g>
          ))}
        </g>
      )}
    </svg>
  );
}

function PieChart({ data, width = 400, height = 350 }) {
  if (!data || !data.labels || !data.values) return null;
  const cx = width / 2 - 40;
  const cy = height / 2;
  const r = Math.min(cx, cy) - 30;
  const total = data.values.reduce((a, b) => a + b, 0) || 1;
  let cumAngle = -Math.PI / 2;

  const slices = data.values.map((val, i) => {
    const angle = (val / total) * 2 * Math.PI;
    const startAngle = cumAngle;
    cumAngle += angle;
    const endAngle = cumAngle;
    const largeArc = angle > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const midAngle = startAngle + angle / 2;
    const labelR = r + 18;
    const lx = cx + labelR * Math.cos(midAngle);
    const ly = cy + labelR * Math.sin(midAngle);
    const pct = ((val / total) * 100).toFixed(1);
    return { val, x1, y1, x2, y2, largeArc, lx, ly, pct, midAngle, color: CHART_COLORS[i % CHART_COLORS.length], label: data.labels[i] };
  });

  return (
    <svg width={width + 120} height={height} style={{ display: 'block', margin: '0 auto' }}>
      {slices.map((s, i) => (
        <g key={i}>
          <path d={`M${cx},${cy} L${s.x1},${s.y1} A${r},${r} 0 ${s.largeArc},1 ${s.x2},${s.y2} Z`} fill={s.color} stroke="white" strokeWidth={2} opacity={0.85}>
            <title>{s.label}: {s.val.toLocaleString()} ({s.pct}%)</title>
          </path>
          {parseFloat(s.pct) > 5 && (
            <text x={s.lx} y={s.ly} textAnchor="middle" fontSize={10} fill="#333" fontWeight="600">{s.pct}%</text>
          )}
        </g>
      ))}
      <g transform={`translate(${width - 40}, 30)`}>
        {data.labels.map((label, i) => (
          <g key={i} transform={`translate(0, ${i * 22})`}>
            <rect width={12} height={12} fill={CHART_COLORS[i % CHART_COLORS.length]} rx={2} />
            <text x={16} y={10} fontSize={11} fill="#555">{label}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function HBarChart({ data, width = 600, height = 0 }) {
  if (!data || !data.labels || !data.datasets) return null;
  const barH = 28;
  const gap = 8;
  const rowH = (barH + gap) * data.datasets.length + 10;
  const totalH = data.labels.length * rowH + 60;
  const h = height || totalH;
  const padding = { top: 40, right: 60, bottom: 20, left: 100 };
  const chartW = width - padding.left - padding.right;
  const allValues = data.datasets.flatMap(ds => ds.values);
  const maxVal = Math.max(...allValues, 1);

  return (
    <svg width={width} height={h} style={{ display: 'block', margin: '0 auto' }}>
      {data.datasets.length > 1 && (
        <g transform={`translate(${padding.left}, ${padding.top - 28})`}>
          {data.datasets.map((ds, i) => (
            <g key={i} transform={`translate(${i * 100}, 0)`}>
              <rect width={12} height={12} fill={CHART_COLORS[i % CHART_COLORS.length]} rx={2} />
              <text x={16} y={10} fontSize={11} fill="#555">{ds.name}</text>
            </g>
          ))}
        </g>
      )}
      {data.labels.map((label, li) => (
        <g key={li}>
          <text x={padding.left - 8} y={padding.top + rowH * li + rowH / 2} textAnchor="end" fontSize={12} fill="#555" dominantBaseline="middle">
            {label.length > 10 ? label.slice(0, 10) + '..' : label}
          </text>
          {data.datasets.map((ds, di) => {
            const barW = (ds.values[li] / maxVal) * chartW;
            const y = padding.top + rowH * li + (barH + gap) * di;
            return (
              <g key={di}>
                <rect x={padding.left} y={y} width={barW} height={barH} fill={CHART_COLORS[di % CHART_COLORS.length]} rx={3} opacity={0.85} />
                <text x={padding.left + barW + 6} y={y + barH / 2 + 1} fontSize={11} fill="#555" dominantBaseline="middle">{ds.values[li].toLocaleString()}</text>
              </g>
            );
          })}
        </g>
      ))}
    </svg>
  );
}

function DataTable({ data }) {
  if (!data || !data.headers || !data.rows) return null;
  return (
    <div style={{ overflowX: 'auto', margin: '0 auto', maxWidth: 700 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {data.headers.map((h, i) => (
              <th key={i} style={{ padding: '10px 14px', background: TC, color: 'white', textAlign: i === 0 ? 'left' : 'right', borderBottom: '2px solid #0d47a1', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? '#fafafa' : 'white' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: '9px 14px', borderBottom: '1px solid #e0e0e0', textAlign: ci === 0 ? 'left' : 'right', whiteSpace: 'nowrap' }}>{typeof cell === 'number' ? cell.toLocaleString() : cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── 데이터 파싱 유틸 ─── */
function parseCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).map(line => {
    const cells = line.split(',').map(c => c.trim());
    return cells.map(c => { const n = Number(c.replace(/[,%]/g, '')); return isNaN(n) ? c : n; });
  });
  return { headers, rows };
}

function parseJSON(text) {
  try {
    const obj = JSON.parse(text);
    const arr = Array.isArray(obj) ? obj : (obj.data || obj.items || obj.records || [obj]);
    if (!arr.length) return null;
    const headers = Object.keys(arr[0]);
    const rows = arr.map(item => headers.map(h => item[h]));
    return { headers, rows };
  } catch { return null; }
}

function detectAndParse(text) {
  text = text.trim();
  if (text.startsWith('[') || text.startsWith('{')) {
    return parseJSON(text);
  }
  return parseCSV(text);
}

/* ─── 메인 컴포넌트 ─── */
function DashboardAgent() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('input'); // input, dashboard, history
  const [rawData, setRawData] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [charts, setCharts] = useState([]);
  const [insight, setInsight] = useState('');
  const [dashboardTitle, setDashboardTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedList, setSavedList] = useState([]);
  const [editingChart, setEditingChart] = useState(null);
  const [customRequest, setCustomRequest] = useState('');
  const fileInputRef = useRef(null);

  // 파일 업로드
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawData(ev.target.result);
      setError('');
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  // 샘플 데이터 로드
  const loadSample = useCallback((sample) => {
    setRawData(sample.data);
    setError('');
  }, []);

  // AI 대시보드 생성
  const handleGenerate = useCallback(async () => {
    if (!rawData.trim()) { setError('데이터를 입력해주세요.'); return; }
    const parsed = detectAndParse(rawData);
    if (!parsed) { setError('데이터 형식을 인식할 수 없습니다. CSV 또는 JSON 형식으로 입력해주세요.'); return; }
    setParsedData(parsed);
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API}/dashboard/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: rawData, headers: parsed.headers, row_count: parsed.rows.length, custom_request: customRequest }),
      });
      const result = await res.json();
      if (result.error) { setError(result.error); setLoading(false); return; }
      setCharts(result.charts || []);
      setInsight(result.insight || '');
      setDashboardTitle(result.title || '데이터 대시보드');
      setMode('dashboard');
    } catch (err) {
      setError('서버 연결 실패: ' + err.message);
    }
    setLoading(false);
  }, [rawData, customRequest]);

  // 차트 추가 요청
  const handleAddChart = useCallback(async (instruction) => {
    if (!parsedData) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/dashboard/add-chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: rawData, instruction, existing_charts: charts }),
      });
      const result = await res.json();
      if (result.chart) {
        setCharts(prev => [...prev, result.chart]);
      }
    } catch (err) {
      setError('차트 추가 실패: ' + err.message);
    }
    setLoading(false);
    setEditingChart(null);
  }, [rawData, parsedData, charts]);

  // 대시보드 저장
  const handleSave = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: dashboardTitle, raw_data: rawData, charts: JSON.stringify(charts), insight }),
      });
      const result = await res.json();
      if (result.id) alert('대시보드가 저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  }, [dashboardTitle, rawData, charts, insight]);

  // 저장 목록 로드
  const loadSavedList = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/list`);
      const data = await res.json();
      setSavedList(data);
    } catch { setSavedList([]); }
  }, []);

  // 저장된 대시보드 불러오기
  const loadDashboard = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/dashboard/${id}`);
      const data = await res.json();
      setRawData(data.raw_data || '');
      setCharts(JSON.parse(data.charts || '[]'));
      setInsight(data.insight || '');
      setDashboardTitle(data.title || '');
      setParsedData(detectAndParse(data.raw_data || ''));
      setMode('dashboard');
    } catch (err) {
      alert('불러오기 실패: ' + err.message);
    }
  }, []);

  const deleteDashboard = useCallback(async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await fetch(`${API}/dashboard/${id}`, { method: 'DELETE' });
      loadSavedList();
    } catch {}
  }, [loadSavedList]);

  // 차트 렌더링
  const renderChart = (chart, idx) => {
    if (!parsedData) return null;
    const { headers, rows } = parsedData;

    let chartData;
    try {
      const labelCol = chart.label_column !== undefined ? chart.label_column : 0;
      const labels = rows.map(r => String(r[labelCol] ?? ''));

      if (chart.type === 'pie' || chart.type === 'donut') {
        const valCol = chart.value_columns ? chart.value_columns[0] : 1;
        const values = rows.map(r => Number(r[valCol]) || 0);
        chartData = { labels, values };
      } else {
        const valCols = chart.value_columns || headers.slice(1).map((_, i) => i + 1);
        const datasets = valCols.map(ci => ({
          name: headers[ci] || `열${ci + 1}`,
          values: rows.map(r => Number(r[ci]) || 0),
        }));
        chartData = { labels, datasets };
      }
    } catch {
      return <div style={{ padding: 20, color: '#999' }}>차트 데이터를 구성할 수 없습니다.</div>;
    }

    const containerW = 620;

    return (
      <div key={idx} style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: 15, color: '#333' }}>{chart.title || `차트 ${idx + 1}`}</h4>
          <button onClick={() => setCharts(prev => prev.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: 18 }} title="삭제">&times;</button>
        </div>
        {chart.description && <p style={{ margin: '0 0 12px', fontSize: 13, color: '#666' }}>{chart.description}</p>}
        <div style={{ overflowX: 'auto' }}>
          {chart.type === 'bar' && <BarChart data={chartData} width={containerW} />}
          {chart.type === 'line' && <LineChart data={chartData} width={containerW} />}
          {(chart.type === 'pie' || chart.type === 'donut') && <PieChart data={chartData} width={380} />}
          {chart.type === 'hbar' && <HBarChart data={chartData} width={containerW} />}
          {chart.type === 'table' && <DataTable data={parsedData} />}
        </div>
      </div>
    );
  };

  /* ─── 헤더 ─── */
  const header = (
    <div style={{ background: `linear-gradient(135deg, ${TC}, ${TC2})`, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
          &larr; Hub
        </button>
        <h1 style={{ margin: 0, color: 'white', fontSize: 20, fontWeight: 700 }}>
          <span style={{ marginRight: 8 }}>📊</span>대시보드 에이전트
        </h1>
      </div>
      <div style={{ display: 'flex', gap: 4 }}>
        {[
          { key: 'input', label: '데이터 입력', icon: '📥' },
          { key: 'dashboard', label: '대시보드', icon: '📊' },
          { key: 'history', label: '저장 목록', icon: '📁' },
        ].map(tab => (
          <button key={tab.key} onClick={() => { setMode(tab.key); if (tab.key === 'history') loadSavedList(); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: mode === tab.key ? 'white' : 'rgba(255,255,255,0.15)', color: mode === tab.key ? TC : 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>
    </div>
  );

  /* ─── 입력 모드 ─── */
  const inputView = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* 왼쪽: 데이터 입력 */}
      <div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>데이터 입력</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv,.json,.txt,.tsv" style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current.click()} style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${TC}`, background: 'white', color: TC, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                파일 업로드
              </button>
              {rawData && <button onClick={() => setRawData('')} style={{ fontSize: 12, color: '#999', background: 'none', border: 'none', cursor: 'pointer' }}>초기화</button>}
            </div>
          </h3>
          <textarea value={rawData} onChange={e => { setRawData(e.target.value); setError(''); }} rows={16} placeholder={'CSV 형식:\n이름,값1,값2\n항목A,100,200\n항목B,150,180\n\n또는 JSON 형식:\n[{"이름":"항목A","값":100}, ...]'}
            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'monospace', lineHeight: 1.6 }} />
          <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
            지원 형식: CSV, JSON, TSV (파일 업로드 또는 직접 입력)
          </div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>추가 요청 (선택)</h3>
          <textarea value={customRequest} onChange={e => setCustomRequest(e.target.value)} rows={3} placeholder="예: 월별 추이를 강조해주세요, 비율을 파이 차트로 보여주세요..."
            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }} />
        </div>
      </div>

      {/* 오른쪽: 샘플 + 미리보기 */}
      <div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>샘플 데이터</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {SAMPLE_DATA.map((s, i) => (
              <button key={i} onClick={() => loadSample(s)} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.background = TB; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = 'white'; }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{s.label}</div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{s.data.split('\n').length}행 데이터</div>
              </button>
            ))}
          </div>
        </div>

        {rawData && (() => {
          const preview = detectAndParse(rawData);
          if (!preview) return null;
          return (
            <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>데이터 미리보기</h3>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
                {preview.headers.length}개 열 &middot; {preview.rows.length}행
              </div>
              <div style={{ overflowX: 'auto', maxHeight: 280 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>{preview.headers.map((h, i) => <th key={i} style={{ padding: '8px 10px', background: TB, color: TC, textAlign: 'left', borderBottom: `2px solid ${TC}`, whiteSpace: 'nowrap', position: 'sticky', top: 0 }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {preview.rows.slice(0, 10).map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => <td key={ci} style={{ padding: '6px 10px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>{typeof cell === 'number' ? cell.toLocaleString() : cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.rows.length > 10 && <div style={{ textAlign: 'center', padding: 8, fontSize: 12, color: '#999' }}>... 외 {preview.rows.length - 10}행</div>}
              </div>
            </div>
          );
        })()}
      </div>

      {/* 생성 버튼 */}
      <div style={{ gridColumn: '1 / -1' }}>
        {error && <div style={{ padding: '10px 16px', borderRadius: 8, background: '#fff3f0', color: '#d32f2f', fontSize: 13, marginBottom: 12, border: '1px solid #ffcdd2' }}>{error}</div>}
        <button onClick={handleGenerate} disabled={loading} style={{
          width: '100%', padding: '14px', borderRadius: 10, border: 'none',
          background: loading ? '#bbb' : `linear-gradient(135deg, ${TC}, #1976d2)`,
          color: 'white', fontSize: 16, fontWeight: 700, cursor: loading ? 'default' : 'pointer',
          boxShadow: loading ? 'none' : '0 4px 12px rgba(21,101,192,0.3)',
        }}>
          {loading ? (
            <span><svg width="18" height="18" viewBox="0 0 24 24" style={{verticalAlign:'middle',marginRight:8,animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>AI가 대시보드를 생성하고 있습니다...</span>
          ) : '대시보드 자동 생성'}
        </button>
      </div>
    </div>
  );

  /* ─── 대시보드 모드 ─── */
  const dashboardView = (
    <div>
      {/* 대시보드 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <input value={dashboardTitle} onChange={e => setDashboardTitle(e.target.value)} style={{ fontSize: 20, fontWeight: 700, color: '#333', border: 'none', borderBottom: '2px solid transparent', padding: '4px 0', outline: 'none', background: 'transparent', width: 400 }}
            onFocus={e => e.currentTarget.style.borderBottomColor = TC}
            onBlur={e => e.currentTarget.style.borderBottomColor = 'transparent'}
            placeholder="대시보드 제목" />
          {parsedData && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{parsedData.headers.length}개 열 &middot; {parsedData.rows.length}행 데이터</div>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSave} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${TC}`, background: 'white', color: TC, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            저장
          </button>
          <button onClick={() => setMode('input')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ccc', background: 'white', color: '#666', fontSize: 13, cursor: 'pointer' }}>
            데이터 수정
          </button>
        </div>
      </div>

      {/* AI 인사이트 */}
      {insight && (
        <div style={{ background: `linear-gradient(135deg, ${TB}, #bbdefb)`, borderRadius: 12, padding: 20, marginBottom: 20, border: `1px solid #90caf9` }}>
          <h4 style={{ margin: '0 0 10px', fontSize: 14, color: TC, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 18 }}>💡</span> AI 인사이트
          </h4>
          <div style={{ fontSize: 13, color: '#333', lineHeight: 1.8 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{insight}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* 차트 그리드 */}
      {charts.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: charts.length === 1 ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
          {charts.map((chart, idx) => renderChart(chart, idx))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          차트가 없습니다. 아래에서 차트를 추가해보세요.
        </div>
      )}

      {/* 차트 추가 */}
      <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '1px dashed #ccc' }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#666' }}>차트 추가</h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {[
            { label: '막대 차트 추가', instruction: '데이터에 적합한 막대 차트를 추가해줘' },
            { label: '꺾은선 차트 추가', instruction: '데이터의 추이를 보여주는 꺾은선 차트를 추가해줘' },
            { label: '파이 차트 추가', instruction: '데이터의 비율을 보여주는 파이 차트를 추가해줘' },
            { label: '가로 막대 추가', instruction: '가로 막대 차트로 비교를 보여줘' },
            { label: '데이터 테이블', instruction: '원본 데이터를 테이블로 보여줘' },
          ].map((btn, i) => (
            <button key={i} onClick={() => handleAddChart(btn.instruction)} disabled={loading}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: 'white', fontSize: 12, cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.background = TB; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = 'white'; }}>
              {btn.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={editingChart || ''} onChange={e => setEditingChart(e.target.value)} placeholder="원하는 차트를 설명하세요... (예: 이익률 추이를 보여줘)"
            style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}
            onKeyDown={e => { if (e.key === 'Enter' && editingChart) handleAddChart(editingChart); }} />
          <button onClick={() => editingChart && handleAddChart(editingChart)} disabled={loading || !editingChart}
            style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: editingChart ? TC : '#ccc', color: 'white', fontSize: 13, fontWeight: 600, cursor: editingChart ? 'pointer' : 'default' }}>
            추가
          </button>
        </div>
      </div>

      {/* 데이터 테이블 */}
      {parsedData && (
        <div style={{ marginTop: 20, background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e0e0e0' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#555' }}>원본 데이터</h4>
          <DataTable data={parsedData} />
        </div>
      )}
    </div>
  );

  /* ─── 히스토리 모드 ─── */
  const historyView = (
    <div>
      <h3 style={{ margin: '0 0 16px', fontSize: 17, color: '#333' }}>저장된 대시보드</h3>
      {savedList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 12, border: '1px solid #e0e0e0', color: '#999' }}>
          저장된 대시보드가 없습니다.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {savedList.map(item => (
            <div key={item.id} style={{ background: 'white', borderRadius: 12, padding: '16px 20px', border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = TC; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.boxShadow = 'none'; }}
              onClick={() => loadDashboard(item.id)}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#333', marginBottom: 4 }}>{item.title || '(제목 없음)'}</div>
                <div style={{ fontSize: 12, color: '#999' }}>{item.created_at ? new Date(item.created_at).toLocaleString('ko-KR') : ''}</div>
              </div>
              <button onClick={e => { e.stopPropagation(); deleteDashboard(item.id); }}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e0e0e0', background: 'white', color: '#999', fontSize: 12, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#e53935'; e.currentTarget.style.color = '#e53935'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.color = '#999'; }}>
                삭제
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      {header}
      <div style={{ maxWidth: 1300, margin: '0 auto', padding: 20 }}>
        {mode === 'input' && inputView}
        {mode === 'dashboard' && dashboardView}
        {mode === 'history' && historyView}
      </div>
    </div>
  );
}

export default DashboardAgent;
